<template>
  <v-dialog
    persistent
    fullscreen
    lazy
    v-model="opened"
    @keydown.esc="closeDialog"
  >
    <!-- State: Loading -->
    <v-card
      v-if="taxReturn === null || currentCompany === null"
      class="taxReturnDeliveryDialog taxReturnDeliveryDialog--loading"
    >
      <sc-flex :is-column="true" :gap="16" :is-y-centered="true">
        <v-progress-circular indeterminate size="64" color="primary" />
        <sc-small> Loading tax return... </sc-small>
        <sc-button-group align="center">
          <sc-button @click="closeDialog" type="secondary" size="small"
            >Cancel</sc-button
          >
        </sc-button-group>
      </sc-flex>
    </v-card>

    <!-- State: Editing -->
    <v-card v-else flat class="taxReturnDeliveryDialog">
      <div class="taxReturnDeliveryDialog__header">
        <!-- Name -->
        <sc-flex class="taxReturnDeliveryDialog__name" :gap="8">
          <!-- Name: Editing state -->
          <sc-flex v-if="editingTaxReturnName" :is-y-centered="true" :gap="8">
            <sc-flex :is-y-centered="true" :gap="2">
              <input v-model="newTaxReturnName" autofocus="true" />
              <sc-text>.pdf</sc-text>
            </sc-flex>
            <sc-button-group>
              <sc-button
                type="primary"
                @click="saveEditTaxReturnName"
                size="small"
                icon-left="check"
                :is-icon-only="true"
                data-test-id="save-tax-return-name-button"
              ></sc-button>
              <sc-button
                type="secondary"
                size="small"
                @click="cancelEditTaxReturnName"
                :is-icon-only="true"
                icon-left="close"
                data-test-id="cancel-edit-tax-return-name-button"
              ></sc-button>
            </sc-button-group>
          </sc-flex>
          <!-- Name: Viewing state -->
          <sc-flex v-else :is-y-centered="true" :gap="8">
            <sc-text class="taxReturnDeliveryDialog__name__view">
              {{ taxReturn.tax_return_name }}
            </sc-text>
            <sc-button-group>
              <sc-button
                type="secondary"
                size="small"
                @click="editTaxReturnName()"
                icon-left="edit"
                :is-icon-only="true"
                data-test-id="edit-tax-return-name-button"
              >
              </sc-button>
            </sc-button-group>
          </sc-flex>
        </sc-flex>

        <!-- Controls -->
        <sc-flex :is-y-centered="true" :is-between="true" :is-full-width="true">
          <!-- Tabs -->
          <v-tabs class="tabs" v-model="activeTab" slider-color="primary">
            <v-tab
              v-for="(step, index) in steps"
              class="v-tab-item"
              :key="step.key"
              :href="`#${step.key}`"
              :disabled="
                step.disabled || (!readonly && index > currentStepIndex)
              "
              :data-test-id="`delivery-tab-${step.key}`"
            >
              <span>{{ index + 1 }}.</span>
              <span>{{ step.name }}</span>
            </v-tab>
          </v-tabs>

          <!-- Close button -->
          <sc-button-group>
            <sc-button
              type="secondary"
              @click="closeDialog"
              :is-round="true"
              icon-left="close"
              size="small"
            ></sc-button>
          </sc-button-group>
        </sc-flex>
      </div>

      <!-- Content -->
      <div class="tabs-content-container">
        <v-tabs-items v-model="activeTab">
          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="classifications"
            v-if="activeTab === 'classifications'"
          >
            <classifications
              ref="classifications"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @classification-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="e-sign-forms"
            v-if="activeTab === 'e-sign-forms'"
          >
            <e-sign-forms
              ref="e-sign-forms"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              :filing-instructions-position.sync="filingInstructionsPosition"
              :filing-instructions-visible.sync="filingInstructionsVisible"
              :highlighted-block-numbers.sync="highlightedBlockNumbers"
              @e-sign-forms-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @save-signature="onSaveSignature"
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="paper-filed-returns"
            v-if="activeTab === 'paper-filed-returns'"
          >
            <paper-filed-returns
              ref="paper-filed-returns"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              :filing-instructions-position.sync="filingInstructionsPosition"
              :filing-instructions-visible.sync="filingInstructionsVisible"
              :highlighted-block-numbers.sync="highlightedBlockNumbers"
              @paper-filed-returns-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="payment-vouchers"
            v-if="activeTab === 'payment-vouchers'"
          >
            <payment-vouchers
              ref="payment-vouchers"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              :filing-instructions-position.sync="filingInstructionsPosition"
              :filing-instructions-visible.sync="filingInstructionsVisible"
              :highlighted-block-numbers.sync="highlightedBlockNumbers"
              @payment-vouchers-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="tax-summary"
            v-if="activeTab === 'tax-summary'"
          >
            <tax-returns
              ref="tax-summary"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @tax-details-updated="taxReturn => onReturnUpdated(taxReturn)"
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="k1-distribution"
            v-if="activeTab === 'k1-distribution'"
          >
            <k1-distribution
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="review-and-deliver"
            v-if="activeTab === 'review-and-deliver'"
          >
            <review-and-deliver
              ref="review-and-deliver"
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @return-delivered="onReturnDelivered"
              @return-updated="onReturnUpdated"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapGetters } from 'vuex';
import Classifications from './Classifications.vue';
import TaxReturns from './TaxReturns.vue';
import ESignForms from './ESignForms.vue';
import PaperFiledReturns from './PaperFiledReturns.vue';
import PaymentVouchers from './PaymentVouchers.vue';
import K1Distribution from './K1Distribution.vue';
import TaxSummary from './TaxSummary.vue';
import ReviewAndDeliver from './ReviewAndDeliver.vue';
import axios from 'axios';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { filingInstructionsStorage } from '@/utils/filingInstructionsStorage';

export default {
  components: {
    Classifications,
    TaxReturns,
    ESignForms,
    PaperFiledReturns,
    PaymentVouchers,
    K1Distribution,
    TaxSummary,
    ReviewAndDeliver,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    opened: {
      type: Boolean,
      default: false,
    },

    currentCompanyId: {
      type: Number,
      default: null,
    },

    taxReturnId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      currentCompany: null,

      // Tax return state
      taxReturn: null,
      filingInstructionsWindow: null,
      editingTaxReturnName: false,
      newTaxReturnName: '',

      // Tab state
      activeTab: 'classifications',

      // filing instruction state
      filingInstructionsPosition: null, // 'bottom', 'left', 'right', 'popup' or null
      filingInstructionsVisible: false,
      highlightedBlockNumbers: [], // Array of block numbers to highlight

      // Message handler for cross-window communication
      handleFilingInstructionsMessage: null,
    };
  },

  methods: {
    editTaxReturnName() {
      this.newTaxReturnName = this.taxReturnNameWithoutExtension;
      this.editingTaxReturnName = true;
    },

    cancelEditTaxReturnName() {
      this.editingTaxReturnName = false;
    },

    async getCurrentCompany() {
      const response = await API.company.getCompany(this.currentCompanyId);
      this.currentCompany = response.data;
    },

    async getTaxReturn() {
      const response = await axios.get(`/tax_returns/${this.taxReturnId}`, {
        params: {
          company_id: this.currentCompanyId,
        },
      });

      this.taxReturn = response.data;
    },

    async saveEditTaxReturnName() {
      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.currentCompany.id,
        tax_return_name: this.newTaxReturnName + '.pdf',
      });

      this.$store.dispatch('application/setSuccessNotification', {
        message: `Saved successfully!`,
      });

      this.taxReturn.tax_return_name = response.data.tax_return_name;
      this.editingTaxReturnName = false;
      this.$emit('refresh-table');
    },

    next() {
      const nextStep = this.steps.find((step, index) => {
        return index > this.currentStepIndex && !step.disabled;
      });

      this.activeTab = nextStep.key;
    },

    onReturnDelivered() {
      this.$emit('return-delivered', this.taxReturn.id);
      this.closeDialog();
    },

    onReturnUpdated(taxReturn, goToNextStep = false) {
      this.taxReturn = taxReturn;
      this.$emit('refresh-table');

      if (goToNextStep) {
        this.next();
      }
    },

    onSaveSignature(taxReturn) {
      this.taxReturn = taxReturn;
      this.$emit('refresh-table');
    },

    async closeDialog() {
      // Call a method on the active child component
      const activeComponent = this.$refs[this.activeTab];

      if (
        activeComponent &&
        typeof activeComponent.beforeClose === 'function'
      ) {
        const canClose = await activeComponent.beforeClose();

        if (!canClose) {
          return;
        }
      }

      // When this dialog is embededed in iframe, we dispatch an event
      // so that the parent window can close it
      if (this.isEmbeddedInNext && this.isInIframe) {
        const event = new CustomEvent('closeDialog');
        window.parent.document.dispatchEvent(event);
      } else {
        this.$emit('update:opened', false);
      }

      // Close the Filing Instructions pop-up window
      if (this.filingInstructionsWindow) {
        // Clean up the message listener
        if (this.handleFilingInstructionsMessage) {
          window.removeEventListener(
            'message',
            this.handleFilingInstructionsMessage,
          );
        }
        this.filingInstructionsWindow.close();
      }
    },

    checkAndOpenFilingInstructions() {
      if (
        this.activeTab !== 'classifications' &&
        this.filingInstructionsWindow === null &&
        this.filingInstructionsPosition === 'popup' &&
        this.filingInstructionsVisible
      ) {
        this.openFilingInstructionsWindow();
      } else if (
        this.filingInstructionsWindow &&
        !this.filingInstructionsVisible
      ) {
        this.closeFilingInstructionsWindow();
      }
    },

    openFilingInstructionsWindow() {
      const url = `/filing-instructions?taxReturnId=${this.taxReturnId}&companyId=${this.taxReturn.company_id}`;

      this.filingInstructionsWindow = window.open(
        url,
        'Filing Instructions',
        'height=1000,width=1000',
      );

      this.filingInstructionsWindow.focus();

      // Create message handler for cross-window communication
      this.handleFilingInstructionsMessage = event => {
        // Verify the message origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'filing-instructions-ready') {
          // Child window is ready, send initial state
          this.sendHighlightedBlocksToWindow();
        }

        if (event.data.type === 'filing-instructions-position-changed') {
          // Update the parent window state
          this.filingInstructionsPosition = event.data.position;
          // Save to localStorage
          filingInstructionsStorage.savePosition(event.data.position);
        }

        if (event.data.type === 'filing-instructions-visibility-changed') {
          // Update the parent window state
          this.filingInstructionsVisible = event.data.visible;
          // Save to localStorage
          filingInstructionsStorage.saveVisibility(event.data.visible);
        }
      };

      // Listen for messages from the pop-up window
      window.addEventListener('message', this.handleFilingInstructionsMessage);

      this.filingInstructionsWindow.addEventListener('beforeunload', () => {
        // Clean up the message listener
        window.removeEventListener(
          'message',
          this.handleFilingInstructionsMessage,
        );
        this.filingInstructionsWindow = null;
      });
    },

    sendHighlightedBlocksToWindow() {
      if (
        this.filingInstructionsWindow &&
        !this.filingInstructionsWindow.closed
      ) {
        this.filingInstructionsWindow.postMessage(
          {
            type: 'filing-instructions-highlighted-blocks',
            highlightedBlockNumbers: this.highlightedBlockNumbers,
          },
          window.location.origin,
        );
      }
    },

    closeFilingInstructionsWindow() {
      if (this.filingInstructionsWindow) {
        // Clean up the message listener
        if (this.handleFilingInstructionsMessage) {
          window.removeEventListener(
            'message',
            this.handleFilingInstructionsMessage,
          );
        }
        this.filingInstructionsWindow.close();
        this.filingInstructionsWindow = null;
      }
    },

    highlightBlocks(blockNumbers) {
      this.highlightedBlockNumbers = blockNumbers || [];
      this.sendHighlightedBlocksToWindow();
    },

    clearHighlights() {
      this.highlightedBlockNumbers = [];
      this.sendHighlightedBlocksToWindow();
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    taxReturnNameWithoutExtension() {
      return this.taxReturn.tax_return_name.replace(/\.[^/.]+$/, '');
    },

    readonly() {
      return this.taxReturn.status !== 'action_required';
    },

    currentStepIndex() {
      return this.steps.findIndex(step => step.key === this.activeTab);
    },

    steps() {
      if (!this.taxReturn) {
        return [];
      } else {
        const steps = [];

        steps.push({
          key: 'classifications',
          name: 'Classifications',
        });

        steps.push({
          key: 'e-sign-forms',
          name: 'E-sign Forms',
          disabled: this.taxReturn.e_sign_forms.length === 0,
        });

        if (this.taxReturnPaperFiledReturnsEnabled) {
          steps.push({
            key: 'paper-filed-returns',
            name: 'Paper-Filed Returns',
          });
        }

        steps.push({
          key: 'payment-vouchers',
          name: 'Payments',
        });

        if (this.taxReturn.tax_details_enabled) {
          steps.push({
            key: 'tax-summary',
            name: 'Tax Summary',
          });
        }

        if (
          this.taxReturnK1DistributionEnabled &&
          this.currentCompany &&
          this.currentCompany.entity_type !== 'Individual'
        ) {
          steps.push({
            key: 'k1-distribution',
            name: 'K-1 Distribution',
          });
        }

        steps.push({
          key: 'review-and-deliver',
          name: 'Review & Deliver',
        });

        return steps;
      }
    },
  },

  async created() {
    await this.getCurrentCompany();
    await this.getTaxReturn();
    // Load saved position and visibility on component creation
    this.filingInstructionsPosition = filingInstructionsStorage.getPosition();
    this.filingInstructionsVisible = filingInstructionsStorage.getVisibility();
  },

  watch: {
    async opened(value) {
      if (value) {
        await this.getTaxReturn();
        // Load saved position and visibility when dialog opens
        this.filingInstructionsPosition = filingInstructionsStorage.getPosition();
        this.filingInstructionsVisible = filingInstructionsStorage.getVisibility();
      }
    },

    async filingInstructionsPosition(value) {
      // Save position to storage whenever it changes
      filingInstructionsStorage.savePosition(value);

      this.checkAndOpenFilingInstructions();
    },

    filingInstructionsVisible(value) {
      // Save visibility to storage whenever it changes
      filingInstructionsStorage.saveVisibility(value);

      this.checkAndOpenFilingInstructions();
    },

    activeTab() {
      this.checkAndOpenFilingInstructions();
      this.clearHighlights();
    },

    highlightedBlockNumbers() {
      // Send updated highlights to popup window when they change
      this.sendHighlightedBlocksToWindow();
    },
  },
};
</script>

<style lang="postcss" scoped>
.taxReturnDeliveryDialog {
  flex-direction: column;
  display: flex;
  overflow: hidden;
  min-height: calc(100dvh);
  max-height: calc(100dvh);
  width: 100%;

  background-color: var(--gray-0);
  /* TODO @chris Try moving this important to components. */
  text-align: left !important;
}

.taxReturnDeliveryDialog__header {
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: space-between;
  padding: 0 12px 4px 12px;
  width: 100%;
}

.taxReturnDeliveryDialog__header > * {
  width: 100%;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__header {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
}

.taxReturnDeliveryDialog--loading {
  align-items: center;
  justify-content: center;
}

.taxReturnDeliveryDialog__name {
  display: flex;
  max-width: 100%;
  width: 350px;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__name {
    margin: 0 auto;

    width: unset;
  }
}

.taxReturnDeliveryDialog__name input {
  font-family: var(--font-family-sans);
  font-size: 16px;

  border: 0 !important;
}

.taxReturnDeliveryDialog__name input:active,
.taxReturnDeliveryDialog__name input:focus {
  border: 0;
  outline: none;
}

.taxReturnDeliveryDialog__name__view {
  display: -webkit-box;
  overflow: hidden;
  max-width: 100%;

  word-break: break-all;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__name__view {
    max-width: calc(100% - 32px);

    -webkit-line-clamp: 1;
  }
}

.dialog-content {
  overflow: hidden;
}

.v-tab-item {
  display: flex !important;
  justify-content: center;
}

.tabs {
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.tabs >>> .v-tabs__container {
  height: 56px;
}

.tabs >>> .v-tabs__item {
  text-transform: none;
  font-weight: bold;
  font-size: 14px;
  padding: 24px 16px;
}

.tabs >>> .v-tabs__slider-wrapper {
  top: 0;
}

.tabs >>> .v-tabs__slider {
  height: 4px;
}

@media (max-width: 960px) {
  .tabs >>> .v-tabs__item {
    text-transform: none;
    font-weight: bold;
    font-size: 16px;
    padding: 16px 12px;
  }
}

@media (max-width: 760px) {
  .v-tab-item span:nth-child(2) {
    display: none;
  }
}

.v-window__container {
  flex: auto;
  min-height: 0;
}

.tabs-content-container {
  height: calc(100dvh - 60px);
  width: 100%;
}

@media (max-width: 1260px) {
  .tabs-content-container {
    height: calc(100dvh - 96px);
  }
}

.tabs-content-container {
  overflow: hidden;
}
</style>
